import React, { useState, useMemo } from 'react';
import styled, { useTheme } from 'styled-components';
import {
  TitleLarge,
  TitleMedium,
  ParagraphMediumText,
} from '../../../components/Texts';
import { Controller, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import TextInput from '../../../components/Inputs/Text';
import Button from '../../../components/Button';
import { useCreateDiscountCodeMutation } from '../../../redux/api/authorized/discountCodes';
import { toast } from 'react-toastify';
import Selector from '../../../components/Inputs/Select';
import { useDispatch } from 'react-redux';
import { openModal } from '../../../redux/slices/modals';

const Container = styled.div`
  width: 100%;
  min-height: calc(100vh - 200px);
  display: flex;
  flex-direction: column;
`;

const FormContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
  max-width: 600px;
`;

const DiscountCode = () => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const [createDiscountCode, { isLoading: isCreating }] =
    useCreateDiscountCodeMutation();
  const [discountType, setDiscountType] = useState('');

  // Create schema dynamically based on discount type
  const schema = useMemo(() => {
    const baseSchema = {
      type: yup.string().required('Discount type is required'),
      minimumAmount: yup
        .number()
        .typeError('Minimum amount must be a number')
        .required('Minimum amount is required')
        .min(0, 'Minimum amount must be at least 0'),
    };

    if (discountType === 'PERCENTAGE') {
      return yup.object({
        ...baseSchema,
        percentage: yup
          .number()
          .typeError('Percentage must be a number')
          .required('Percentage is required')
          .min(0, 'Percentage must be at least 0')
          .max(100, 'Percentage must be at most 100'),
        maxDiscountAmount: yup
          .number()
          .typeError('Max discount amount must be a number')
          .required('Max discount amount is required')
          .positive('Max discount amount must be positive'),
      });
    } else if (discountType === 'FIXED') {
      return yup.object({
        ...baseSchema,
        amount: yup
          .number()
          .typeError('Amount must be a number')
          .required('Amount is required')
          .positive('Amount must be positive'),
      });
    }

    return yup.object({
      type: yup.string().required('Discount type is required'),
    });
  }, [discountType]);

  const {
    control,
    handleSubmit,
    watch,
    reset,
    formState: { errors, isValid, isSubmitting },
  } = useForm({
    mode: 'onChange',
    resolver: yupResolver(schema),
    defaultValues: {
      type: '',
      percentage: '',
      amount: '',
      maxDiscountAmount: '',
      minimumAmount: '',
    },
  });

  const watchedType = watch('type');

  // Update discountType when form type changes
  React.useEffect(() => {
    if (watchedType) {
      setDiscountType(watchedType);
    } else {
      setDiscountType('');
    }
  }, [watchedType]);

  const onSubmit = async (data) => {
    try {
      const payload = {
        type: data.type,
        minimumAmount: parseFloat(data.minimumAmount),
      };

      if (data.type === 'PERCENTAGE') {
        payload.percentage = parseFloat(data.percentage);
        payload.maxDiscountAmount = parseFloat(data.maxDiscountAmount);
      } else if (data.type === 'FIXED') {
        payload.amount = parseFloat(data.amount);
      }

      console.log('Creating discount code with payload:', payload);
      const response = await createDiscountCode(payload).unwrap();
      console.log('Discount code creation response:', response);

      // The code should be in response.code or response.data.code
      const discountCode = response?.code || response?.data?.code;

      if (discountCode) {
        // Show modal with discount code
        dispatch(
          openModal({
            name: 'discountCodeCreated',
            props: {
              code: discountCode,
            },
          }),
        );
        // Reset form
        reset();
        setDiscountType('');
        toast('Discount code created successfully!', {
          type: 'success',
        });
      } else {
        console.error('No code in response:', response);
        toast('Discount code created but no code was returned.', {
          type: 'warning',
        });
      }
    } catch (error) {
      console.error('Error creating discount code:', error);
      const errorMessage =
        error?.data?.message ||
        error?.message ||
        'Failed to create discount code';
      toast(errorMessage, {
        type: 'error',
      });
    }
  };

  return (
    <Container>
      <TitleLarge>Create Discount Code</TitleLarge>
      <TitleMedium>
        Create a new discount code for gift card purchases
      </TitleMedium>
      <form
        onSubmit={handleSubmit(onSubmit)}
        style={{
          marginTop: 20,
          gap: 20,
          display: 'flex',
          flexDirection: 'column',
          padding: 24,
          borderRadius: 16,
          border: `1px solid ${theme.border.primary}`,
          maxWidth: 600,
        }}
      >
        <FormContainer>
          <Controller
            name='type'
            control={control}
            render={({ field }) => (
              <Selector
                label='Discount Type'
                options={[
                  { label: 'Percentage', value: 'PERCENTAGE' },
                  { label: 'Fixed Amount', value: 'FIXED' },
                ]}
                error={errors?.type?.message}
                {...field}
              />
            )}
          />

          {watchedType === 'PERCENTAGE' && (
            <>
              <Controller
                name='percentage'
                control={control}
                render={({ field }) => (
                  <TextInput
                    label='Percentage'
                    type='number'
                    step='0.01'
                    placeholder='e.g., 10'
                    error={errors?.percentage?.message}
                    {...field}
                    onChange={(e) =>
                      field.onChange(parseFloat(e.target.value) || '')
                    }
                  />
                )}
              />
              <Controller
                name='maxDiscountAmount'
                control={control}
                render={({ field }) => (
                  <TextInput
                    label='Max Discount Amount'
                    type='number'
                    step='0.01'
                    placeholder='e.g., 50.00'
                    error={errors?.maxDiscountAmount?.message}
                    {...field}
                    onChange={(e) =>
                      field.onChange(parseFloat(e.target.value) || '')
                    }
                  />
                )}
              />
              <Controller
                name='minimumAmount'
                control={control}
                render={({ field }) => (
                  <TextInput
                    label='Minimum Amount'
                    type='number'
                    step='0.01'
                    placeholder='e.g., 100.00'
                    error={errors?.minimumAmount?.message}
                    {...field}
                    onChange={field.onChange}
                  />
                )}
              />
            </>
          )}

          {watchedType === 'FIXED' && (
            <>
              <Controller
                name='amount'
                control={control}
                render={({ field }) => (
                  <TextInput
                    label='Amount'
                    type='number'
                    step='0.01'
                    placeholder='e.g., 25.00'
                    error={errors?.amount?.message}
                    {...field}
                    onChange={(e) =>
                      field.onChange(parseFloat(e.target.value) || '')
                    }
                  />
                )}
              />
              <Controller
                name='minimumAmount'
                control={control}
                render={({ field }) => (
                  <TextInput
                    label='Minimum Amount'
                    type='number'
                    step='0.01'
                    placeholder='e.g., 100.00'
                    error={errors?.minimumAmount?.message}
                    {...field}
                    onChange={(e) =>
                      field.onChange(parseFloat(e.target.value) || '')
                    }
                  />
                )}
              />
            </>
          )}
        </FormContainer>
        <Button
          type='submit'
          loading={isSubmitting || isCreating}
          disabled={!isValid || isSubmitting || isCreating || !watchedType}
        >
          Create Discount Code
        </Button>
      </form>
    </Container>
  );
};

export default DiscountCode;
